import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { DebounceInput } from "react-debounce-input";
import { Link, useParams, useLocation } from "react-router";

import {
  LoginRoles,
  emptyString,
  pathConstant,
  loginConstants,
  individualGroupProfileConstants,
  INDIVIDUAL_GROUP_PROFILE_DEFAULT_SORTING,
  INDIVIDUAL_GROUP_PROFILE_LABELS,
  INDIVIDUAL_GROUP_PROFILE_SELECT_MAX_MENU_HEIGHT,
  LIST_SEARCH_DEBOUNCE_MS,
  brandProfileConstants,
  MENU_PLACEMENT,
  FILTER_LABEL,
} from "../../constants";
import { setItemToStorage, getItemFromStorage } from "../../../../system";
import {
  useSpinner,
  Option,
  Spinner,
  DataTable,
  SurveyStats,
  ValueContainer,
  loadingIndicator,
  paginatedPostRequestQueryBody,
} from "../../../../shared";
import {
  individualGroupProfileActions,
  individualStoreProfileActions,
} from "../../redux/actions";
import ImportOrder from "../../../../shared/components/ImportOrder/ImportOrder";
import { buildProfileFilterQuery } from "../shared/performanceQueryUtility";
import { createIndividualGroupProfileColumns } from "./utility";
import { customStyle } from "./individualGroupProfile.styles";

const {
  OR,
  AND,
  NAME,
  STATE,
  GROUP_DETAIL,
  STORES_BY_GROUP_ID,
  PAGINATED_GROUPS_DATA,
} = individualGroupProfileConstants;

const IndividualGroupProfile = (props) => {
  const {
    user,
    individualGroupProfileReducer: {
      groupDetails,
      allLocations,
      collectiveData,
      allGroupLocations,
      groupDataPagination,
    },
    getGroupDetails,
    getGroupPaginatedDetails,
    getBrandLocationsByGroupId,
    resetRestaurantProfile,
  } = props;

  const role = getItemFromStorage(loginConstants.ROLE);

  const { brandId, groupId } = useParams();
  const location = useLocation();

  const filters = useRef({
    name: [],
    state: [],
  });

  const [sorting, setSorting] = useState(INDIVIDUAL_GROUP_PROFILE_DEFAULT_SORTING);
  const [filteredDataValue, setFilteredDataValue] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Set filter for the select dropdown
  const setFilteredData = (property, value) => {
    //Save values against its filter name
    if (property === NAME) {
      filters.current.name = value;
    } else {
      filters.current.state = value;
    }
    const query = buildProfileFilterQuery(filters.current, {
      or: OR,
      and: AND,
    });
    setFilteredDataValue(query);
  };

  //Set unique states
  const uniqueStates = [...new Set(allGroupLocations.map((loc) => loc.state))];

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const requestBody = paginatedPostRequestQueryBody(
      groupDataPagination,
      pageNumber,
      searchString,
      sortBy || sorting,
      filteredDataValue || emptyString,
      emptyString,
    );
    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    getGroupPaginatedDetails(requestBody, groupId || brandId);
  };

  const getNavigateToPath = (storeId) => {
    if (role === LoginRoles.GROUP) {
      return `/group-profile/${groupId}/store-performance/${storeId}`;
    }

    switch (location.state?.name) {
      case pathConstant.BRAND_PROFILE:
      case pathConstant.STORE_BRAND_PROFILE:
        return `/stores/brand-profile/${brandId}/store-profile/${storeId}`;
      case pathConstant.GROUP:
        return `/stores/brand-groups/${brandId}/store-profile/${storeId}`;
      case pathConstant.PERFORMANCE:
        return `/stores/dashboard/${brandId}/brand-performance/store-profile/${storeId}`;
      case pathConstant.STORE_PERFORMANCE:
        return `/stores/dashboard/${brandId}/brand-performance/store-performance/${storeId}`;
      default:
        return emptyString;
    }
  };

  //Handle click store name
  const handleClickStoreName = (profile) => {
    setItemToStorage(loginConstants.LOCATION_ID, profile.id);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify(location.state || { name: pathConstant.GROUP_PROFILE }),
    );
  };

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const profile = row.original;

      if (profile.name) {
        return (
          <Link
            className="table-links"
            to={getNavigateToPath(profile.id)}
            state={location.state || { name: pathConstant.GROUP_PROFILE }}
            onClick={() => handleClickStoreName(profile)}
          >
            {profile.name}
          </Link>
        );
      }
      return <span />;
    },
    // eslint-disable-next-line
    [location],
  );

  //Static headers for data table
  //"Cell" is being used to manipulate the data for displaying purpose
  const franchiseProfile = useMemo(
    () => createIndividualGroupProfileColumns(cellNameRenderer),
    [cellNameRenderer],
  );

  // Reset store profile
  useEffect(() => {
    resetRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  // fetch data from these calls before the page loads
  useEffect(() => {
    getGroupDetails(groupId);
    getBrandLocationsByGroupId(groupId);
    getData(0);
    setItemToStorage(loginConstants.BRAND_ID, brandId || user.brandId);

    // eslint-disable-next-line
  }, [searchString, filteredDataValue]);

  return (
    <>
      {
        <div className="container-fluid py-3">
          <div className="notification-form card mb-3 shadow">
            <div className="card-body p-0">
              <div className="row">
                <div className="col-6">
                  <h5 className="ps-4 py-3 mb-0">
                    {INDIVIDUAL_GROUP_PROFILE_LABELS.GENERAL}
                  </h5>
                </div>
                <div className="col-6 d-none">
                  <ImportOrder />
                </div>
              </div>
              <hr className="p-0 m-0" />
              <div className="row px-4 py-3">
                <div className="col-12">
                  <div className="row">
                    <div className="col-12" data-testid="group-name">
                      <span className="col-lg-2 col-md-3 col-6 d-inline-block survey_profile_labels bold-5">
                        {INDIVIDUAL_GROUP_PROFILE_LABELS.GROUP_NAME}
                      </span>
                      <span className="profile_lable_values">
                        {groupDetails.name}
                      </span>
                    </div>
                    <div className="col-12" data-testid="group-id">
                      <span className="col-lg-2 col-md-3 col-6 d-inline-block survey_profile_labels bold-5">
                        {INDIVIDUAL_GROUP_PROFILE_LABELS.GROUP_ID}
                      </span>
                      <span className="profile_lable_values">
                        {groupDetails.id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="notification-form card shadow">
            <div className="card-body p-0">
              <div className="row">
                <div className="col-6 py-3">
                  <h5 className="ps-4 mb-0">
                    {INDIVIDUAL_GROUP_PROFILE_LABELS.STORES}
                  </h5>
                </div>
                <div className="col-6 py-3">
                  <div className="search-container ms-auto me-4">
                    <DebounceInput
                      debounceTimeout={LIST_SEARCH_DEBOUNCE_MS}
                      type="text"
                      className=" search-restaurants"
                      placeholder={INDIVIDUAL_GROUP_PROFILE_LABELS.SEARCH_PLACEHOLDER}
                      id="search-store-locations"
                      value={searchString}
                      onChange={(e) => setSearchString(e.target.value)}
                    />
                    <i
                      className="fa fa-search search-icon"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
              </div>
              <hr className="mt-0" />
              <div className="row px-4 py-2">
                <div className="col-12 col-md-4">
                  <Select
                    name="name"
                    options={[...allGroupLocations]}
                    components={{
                      IndicatorSeparator: () => null,
                      Option,
                      ValueContainer: ValueContainer,
                      LoadingIndicator: loadingIndicator,
                    }}
                    isMulti
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.name}
                    onChange={(selectedOption) =>
                      setFilteredData(
                        NAME,
                        selectedOption.map((opt) => opt.name),
                      )
                    }
                    placeholder={FILTER_LABEL.ALL_STORES}
                    isSearchable
                    maxMenuHeight={INDIVIDUAL_GROUP_PROFILE_SELECT_MAX_MENU_HEIGHT}
                    menuPlacement={MENU_PLACEMENT.AUTO}
                    styles={customStyle}
                    isDisabled={spinner(STORES_BY_GROUP_ID)}
                    isLoading={spinner(STORES_BY_GROUP_ID)}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    name="state"
                    options={[
                      ...uniqueStates
                        .filter((state) => state !== emptyString)
                        .map((state) => ({ id: state, state })),
                    ]}
                    components={{
                      IndicatorSeparator: () => null,
                      Option,
                      ValueContainer: ValueContainer,
                      LoadingIndicator: loadingIndicator,
                    }}
                    isMulti
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.state}
                    onChange={(selectedOption) =>
                      setFilteredData(
                        STATE,
                        selectedOption.map((opt) => opt.state),
                      )
                    }
                    placeholder={FILTER_LABEL.ALL_STATES}
                    isSearchable
                    maxMenuHeight={INDIVIDUAL_GROUP_PROFILE_SELECT_MAX_MENU_HEIGHT}
                    menuPlacement={MENU_PLACEMENT.AUTO}
                    styles={customStyle}
                    isDisabled={spinner(STORES_BY_GROUP_ID)}
                    isLoading={spinner(STORES_BY_GROUP_ID)}
                  />
                </div>
              </div>
              <SurveyStats
                total={collectiveData.totalSurvey}
                positive={collectiveData.positiveResponses}
                negative={collectiveData.negativeResponses}
              />
              {spinner(PAGINATED_GROUPS_DATA || GROUP_DETAIL) && (
                <Spinner />
              )}{" "}
              <div>
                <DataTable
                  columns={franchiseProfile}
                  data={allLocations}
                  totalRows={groupDataPagination.totalElements}
                  numberOfRowsPerPage={groupDataPagination.size}
                  pageNumber={groupDataPagination.number + 1}
                  onPageChange={(pageNum) => getData(pageNum)}
                  onColumnSort={(sortBy) =>
                    getData(groupDataPagination.number, sortBy)
                  }
                  sortedBy={sorting}
                />
              </div>
            </div>
          </div>
        </div>
      }
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  individualGroupProfileReducer: state.individualGroupProfileReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getGroupDetails: (groupId) =>
    dispatch(individualGroupProfileActions.getGroupDetails(groupId)),
  getGroupPaginatedDetails: (body, groupId) =>
    dispatch(
      individualGroupProfileActions.getGroupPaginatedDetails(body, groupId),
    ),
  getBrandLocationsByGroupId: (groupId) =>
    dispatch(individualGroupProfileActions.getBrandLocationsByGroupId(groupId)),
  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualGroupProfile);
